import { useEffect, useState } from 'react'
import { VideoCard } from './video-card'
import { supabase } from '@/lib/supabase'
import type { Video } from '@/types/database'
import { AlertCircle } from 'lucide-react'

interface VideoGridProps {
  searchQuery: string
  sortBy: 'recent' | 'popular'
}

interface VideoWithRelations extends Video {
  profiles: { full_name: string | null } | null
  products: { title: string } | null
}

export function VideoGrid({ searchQuery, sortBy }: VideoGridProps) {
  const [videos, setVideos] = useState<VideoWithRelations[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        let query = supabase
          .from('videos')
          .select('*, profiles(full_name), products(title)')
          .eq('status', 'approved')

        if (searchQuery) {
          query = query.ilike('title', `%${searchQuery}%`)
        }

        if (sortBy === 'popular') {
          query = query.order('likes_count', { ascending: false })
        } else {
          query = query.order('created_at', { ascending: false })
        }

        const { data, error: fetchError } = await query

        if (fetchError) throw fetchError
        setVideos(data || [])
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch videos'))
      } finally {
        setLoading(false)
      }
    }

    fetchVideos()

    // Subscribe to changes
    const channel = supabase
      .channel('videos_channel')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'videos' },
        (payload) => {
          if (payload.eventType === 'INSERT' && (payload.new as Video).status === 'approved') {
            setVideos((prev) => [(payload.new as VideoWithRelations), ...prev])
          } else if (payload.eventType === 'DELETE') {
            setVideos((prev) =>
              prev.filter((video) => video.id !== payload.old.id)
            )
          } else if (payload.eventType === 'UPDATE') {
            const updatedVideo = payload.new as Video
            if (updatedVideo.status === 'approved') {
              setVideos((prev) =>
                prev.map((video) =>
                  video.id === updatedVideo.id ? (payload.new as VideoWithRelations) : video
                )
              )
            } else {
              // Remove video if it's no longer approved
              setVideos((prev) =>
                prev.filter((video) => video.id !== updatedVideo.id)
              )
            }
          }
        }
      )
      .subscribe()

    return () => {
      channel.unsubscribe()
    }
  }, [searchQuery, sortBy])

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className="animate-pulse bg-gray-200 rounded-lg aspect-video"
          />
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="inline-flex items-center p-4 bg-red-50 rounded-lg">
          <AlertCircle className="h-6 w-6 text-red-500 mr-2" />
          <p className="text-red-500">Failed to load videos. Please try again later.</p>
        </div>
      </div>
    )
  }

  if (!videos.length) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Aucune vidéo trouvée.</p>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {videos.map((video) => (
        <VideoCard key={video.id} video={video} />
      ))}
    </div>
  )
}